import { MSG, parseEditQty } from '../../components/pickingEdit';
import type { SkuLineDraft } from '../../components/SkuLinesEditor';
import { fmtQty, norm } from '../../format';
import type { FieldTarget } from '../../hooks/useServerErrors';
import { buildHubClientSnapshot, buildHubSnapshot } from '../../snapshot';
import type {
  ClientRef,
  CreateHubTransferPayload,
  FieldError,
  HubTransferLine,
  WarehouseSku,
} from '../../types';
import type { WarehouseHubOption } from '../../WarehouseContext';

/**
 * Pure model of the "Buat transfer warehouse" form: the typed values, the
 * per-step checks, the destination prefilled from the hub master, the POST
 * body and the mapping of a 422 back onto the form. No React here —
 * HubTransferNewPage renders it. The server (OutboundOrderValidator + the
 * request rules) stays the source of truth; these checks only guard required
 * input so a round trip is not wasted.
 */

export const HUB_TRANSFER_MSG = {
  destHub: 'Hub tujuan wajib dipilih.',
  sameHub: 'Hub tujuan harus berbeda dari hub asal.',
  client: 'Client wajib dipilih.',
  date: 'Estimasi tanggal kirim wajib diisi.',
  datePast: 'Estimasi tanggal kirim tidak boleh di masa lalu.',
  recipient: 'Nama penerima wajib diisi.',
  phone: 'No. HP penerima wajib diisi.',
  address: 'Alamat hub tujuan wajib diisi.',
  latitude: 'Latitude harus angka -90 sampai 90.',
  longitude: 'Longitude harus angka -180 sampai 180.',
  noLines: 'Minimal satu produk.',
  pickSku: 'SKU wajib dipilih.',
  qtyRequired: 'Qty wajib diisi.',
  overAvailable: (n: number) => `Maksimal ${fmtQty(n)} tersedia di hub asal.`,
  duplicate: (row: number) => `SKU ini sudah ada di baris ${row}.`,
} as const;

/** What blocks "Lanjut" on the product step while a row is wrong. */
export const LINES_BLOCKED =
  'Perbaiki qty yang melebihi stok atau tidak valid.';

/* ------------------------------ destination ------------------------------ */

/** The destination contact & place the form edits (raw strings). */
export interface DestinationDraft {
  recipientName: string;
  recipientPhone: string;
  address: string;
  latitude: string;
  longitude: string;
  notes: string;
}

export const emptyDestination = (): DestinationDraft => ({
  recipientName: '',
  recipientPhone: '',
  address: '',
  latitude: '',
  longitude: '',
  notes: '',
});

const coordText = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '' : String(n);

/** The destination as the hub master (pitstop) knows it; blank where unknown. */
export const destinationFromHub = (
  hub: WarehouseHubOption | null | undefined
): DestinationDraft => ({
  recipientName: hub?.name?.trim() ?? '',
  recipientPhone: hub?.phoneNumber?.trim() ?? '',
  address: hub?.address?.trim() ?? '',
  latitude: coordText(hub?.latitude),
  longitude: coordText(hub?.longitude),
  notes: '',
});

/** Master-data fields (not the free-text notes) the destination can inherit. */
export type DestinationField = Exclude<keyof DestinationDraft, 'notes'>;

/**
 * The fields still holding exactly what the hub master says — the form tags
 * them "Dari master hub". An empty master value is never "from the master".
 */
export const fromMaster = (
  draft: DestinationDraft,
  hub: WarehouseHubOption | null | undefined
): ReadonlySet<DestinationField> => {
  const master = destinationFromHub(hub);
  const out = new Set<DestinationField>();
  (Object.keys(master) as (keyof DestinationDraft)[]).forEach((k) => {
    if (k === 'notes') return;
    if (master[k] && draft[k].trim() === master[k]) out.add(k);
  });
  return out;
};

/** "0812-3456 7890" → "081234567890" (what the server stores and matches). */
export const compactPhone = (s: string): string =>
  s.trim().replace(/[\s\-().]/g, '');

/** Same rule as the server's OutboundOrderValidator. */
/** "-6,2443" / "-6.2443" → -6.2443; blank or junk → null. */
export const parseCoord = (s: string): number | null => {
  const t = s.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/* --------------------------------- step 1 -------------------------------- */

export interface InfoDraft {
  /** The source hub (the page's hub). */
  hubID: number;
  destHubID: number | null;
  client: ClientRef | null;
  /** `YYYY-MM-DD`. */
  expectedDate: string;
  notes: string;
  destination: DestinationDraft;
}

/** Problems of step 1 (each names the field the server would). */
export const checkInfo = (info: InfoDraft, today: string): FieldError[] => {
  const out: FieldError[] = [];
  const add = (field: string, message: string) => out.push({ field, message });
  if (!info.destHubID) add('destinationHubID', HUB_TRANSFER_MSG.destHub);
  else if (info.destHubID === info.hubID)
    add('destinationHubID', HUB_TRANSFER_MSG.sameHub);
  if (!info.client) add('clientID', HUB_TRANSFER_MSG.client);
  if (!info.expectedDate) add('expectedDeliveryDate', HUB_TRANSFER_MSG.date);
  else if (info.expectedDate < today)
    add('expectedDeliveryDate', HUB_TRANSFER_MSG.datePast);

  const d = info.destination;
  if (!d.recipientName.trim()) add('recipientName', HUB_TRANSFER_MSG.recipient);
  // Any phone format (mobile, 021 landline, …): only required.
  if (!d.recipientPhone.trim()) add('recipientPhone', HUB_TRANSFER_MSG.phone);
  if (!d.address.trim()) add('destinationAddress', HUB_TRANSFER_MSG.address);
  const lat = parseCoord(d.latitude);
  if (lat === null || lat < -90 || lat > 90)
    add('destinationLatitude', HUB_TRANSFER_MSG.latitude);
  const lng = parseCoord(d.longitude);
  if (lng === null || lng < -180 || lng > 180)
    add('destinationLongitude', HUB_TRANSFER_MSG.longitude);
  return out;
};

/* --------------------------------- step 2 -------------------------------- */

/** Allocatable qty at the source hub of a SKU; null while unknown. */
export type AvailableFor = (sku: string) => number | null;

/** A row the user has touched (SKU or qty set). */
const touched = (l: SkuLineDraft): boolean => !!l.sku || !!l.qty.trim();

/**
 * The problem with a row as typed, shown while the user is still editing: a
 * duplicate SKU, an invalid qty, a qty above what the source hub can give.
 * Blank qty / missing SKU are NOT problems yet ({@link checkLines} says so
 * when the user moves on).
 */
export const lineIssue = (
  lines: readonly SkuLineDraft[],
  index: number,
  availableFor: AvailableFor
): string | null => {
  const l = lines[index];
  if (!l) return null;
  if (l.sku) {
    const first = lines.findIndex((x) => x.sku === l.sku);
    if (first !== -1 && first < index)
      return HUB_TRANSFER_MSG.duplicate(first + 1);
  }
  const raw = l.qty.trim().replace(',', '.');
  if (!raw) return null;
  const qty = parseEditQty(raw);
  if (qty === null)
    return /^\d*\.\d{3,}$/.test(raw) ? MSG.qtyDecimals : MSG.qtyPositive;
  if (qty <= 0) return MSG.qtyPositive;
  if (l.sku) {
    const avail = availableFor(l.sku);
    if (avail !== null && qty > avail)
      return HUB_TRANSFER_MSG.overAvailable(avail);
  }
  return null;
};

/** True while any row has a {@link lineIssue} — "Lanjut" stays off. */
export const hasLineIssue = (
  lines: readonly SkuLineDraft[],
  availableFor: AvailableFor
): boolean => lines.some((_, i) => lineIssue(lines, i, availableFor) !== null);

/** Problems of step 2, each pointing at its row (`lines[i].sku|qty`). */
export const checkLines = (
  lines: readonly SkuLineDraft[],
  availableFor: AvailableFor
): FieldError[] => {
  const out: FieldError[] = [];
  if (!lines.some(touched)) {
    out.push({ field: 'lines', message: HUB_TRANSFER_MSG.noLines });
    return out;
  }
  lines.forEach((l, i) => {
    if (!touched(l)) return;
    if (!l.sku) {
      out.push({
        field: `lines[${i}].sku`,
        message: HUB_TRANSFER_MSG.pickSku,
        lineIndex: i,
      });
      return;
    }
    const message = l.qty.trim()
      ? lineIssue(lines, i, availableFor)
      : HUB_TRANSFER_MSG.qtyRequired;
    if (message) out.push({ field: `lines[${i}].qty`, message, lineIndex: i });
  });
  return out;
};

/** The rows that go on the document (touched, with a SKU). */
export const filledLines = (lines: readonly SkuLineDraft[]): SkuLineDraft[] =>
  lines.filter((l) => touched(l) && l.sku);

/** Qty of a row (0 while it is not a valid number). */
export const qtyOf = (l: SkuLineDraft): number => parseEditQty(l.qty) ?? 0;

export const totalQty = (lines: readonly SkuLineDraft[]): number =>
  Math.round(lines.reduce((t, l) => t + qtyOf(l), 0) * 100) / 100;

/* --------------------------------- payload ------------------------------- */

/**
 * The POST body. `hubs` is the WMS hub list the snapshots (name, code) are
 * frozen from — like every other WMS create call. Assumes the draft passed
 * {@link checkInfo} and {@link checkLines}.
 */
export const toPayload = (
  info: InfoDraft & { client: ClientRef; destHubID: number },
  lines: readonly SkuLineDraft[],
  hubs: readonly WarehouseHubOption[]
): CreateHubTransferPayload => {
  const dest = buildHubSnapshot(hubs, info.destHubID);
  const d = info.destination;
  const notes = info.notes.trim();
  const destNotes = d.notes.trim();
  return {
    ...buildHubClientSnapshot(hubs, info.hubID, info.client),
    destinationHubID: dest.hubID,
    destinationHubName: dest.hubName,
    ...(dest.hubCode ? { destinationHubCode: dest.hubCode } : {}),
    expectedDeliveryDate: info.expectedDate,
    ...(notes ? { notes } : {}),
    recipientName: d.recipientName.trim(),
    recipientPhone: compactPhone(d.recipientPhone),
    destinationAddress: d.address.trim(),
    destinationLatitude: parseCoord(d.latitude) as number,
    destinationLongitude: parseCoord(d.longitude) as number,
    ...(destNotes ? { destinationNotes: destNotes } : {}),
    lines: filledLines(lines).map((l) => ({
      sku: l.sku,
      qty: parseEditQty(l.qty) ?? Number(l.qty),
    })),
  };
};

/** The review table's rows — same shape as a saved transfer line. */
export const reviewLines = (
  lines: readonly SkuLineDraft[],
  skuBy: ReadonlyMap<string, Pick<WarehouseSku, 'itemName' | 'uomName'>>,
  availableFor: AvailableFor
): HubTransferLine[] =>
  filledLines(lines).map((l, i) => ({
    lineNo: i + 1,
    sku: l.sku,
    itemName: skuBy.get(l.sku)?.itemName ?? '',
    uomName: skuBy.get(l.sku)?.uomName ?? '',
    qtyAvailable: availableFor(l.sku) ?? 0,
    qty: qtyOf(l),
  }));

/* ---------------------------- server errors ------------------------------ */

export type HubTransferFieldKey =
  | 'hub'
  | 'destinationHub'
  | 'client'
  | 'expectedDate'
  | 'notes'
  | 'recipientName'
  | 'recipientPhone'
  | 'address'
  | 'latitude'
  | 'longitude'
  | 'destNotes'
  | 'lines'
  | 'form';

const STEP_OF: Record<HubTransferFieldKey, number> = {
  hub: 1,
  destinationHub: 1,
  client: 1,
  expectedDate: 1,
  notes: 1,
  recipientName: 1,
  recipientPhone: 1,
  address: 1,
  latitude: 1,
  longitude: 1,
  destNotes: 1,
  lines: 2,
  form: 3,
};
export const hubTransferStepOf = (k: HubTransferFieldKey): number => STEP_OF[k];

/** The line row an error names (`lineIndex`, else `lines[2].…`), or null. */
const lineOf = (e: FieldError): number | null => {
  if (e.lineIndex !== undefined && e.lineIndex !== null) return e.lineIndex;
  const m = /lines?\W*(\d+)/i.exec(e.field || '');
  return m ? Number(m[1]) : null;
};

/** Map a backend field (snake, dotted or camel) onto a hub transfer form field. */
export const hubTransferFieldOf = (
  e: FieldError
): FieldTarget<HubTransferFieldKey> => {
  const n = norm(e.field || '');
  if (!n) return { key: 'form' };
  const line = lineOf(e);
  if (line !== null || n.startsWith('line') || n === 'sku' || n === 'qty')
    return { key: 'lines', line };
  // Before `hub` / `destination…`: destinationHubID|Name|Code, destinationNotes.
  if (n.includes('destinationhub')) return { key: 'destinationHub' };
  if (n.includes('destinationnotes')) return { key: 'destNotes' };
  if (n.includes('latitude') || n === 'lat') return { key: 'latitude' };
  if (n.includes('longitude') || n === 'lng' || n === 'long')
    return { key: 'longitude' };
  if (n.includes('address')) return { key: 'address' };
  if (n.includes('phone')) return { key: 'recipientPhone' };
  if (n.includes('recipient')) return { key: 'recipientName' };
  if (n.includes('expected') || n.includes('date'))
    return { key: 'expectedDate' };
  if (n.includes('client')) return { key: 'client' };
  if (n.includes('hub')) return { key: 'hub' };
  if (n === 'notes') return { key: 'notes' };
  return { key: 'form' };
};
